import React, { useState, useEffect } from 'react';
import { FaChartBar, FaDownload, FaSpinner } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { dashboardService } from '../../services/dashboardService';
import { saleService } from '../../services/saleService';
import { productService } from '../../services/productService';
import jsPDF from 'jspdf';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ReportsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchSalesData();
    fetchInventoryData();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await dashboardService.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesData = async () => {
    try {
      const response = await saleService.getSales({ limit: 100 });
      setSalesData(response.data);
    } catch (error) {
      console.error('Failed to fetch sales data:', error);
    }
  };

  const fetchInventoryData = async () => {
    try {
      const response = await productService.getProducts();
      setInventoryData(response.data);
    } catch (error) {
      console.error('Failed to fetch inventory data:', error);
    }
  };

  const exportSalesToPDF = async () => {
    setExporting('sales-report.pdf');
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate actual revenue from sales data
      const actualRevenue = salesData?.reduce((sum, sale) => sum + (sale.total || 0), 0) || 0;
      const actualSales = salesData?.length || 0;
      
      // Calculate monthly sales for chart data
      const monthlySales = {};
      salesData?.forEach(sale => {
        const month = new Date(sale.createdAt).toLocaleString('default', { month: 'short', year: 'numeric' });
        monthlySales[month] = (monthlySales[month] || 0) + (sale.total || 0);
      });
      
      let yPosition = 20;
      
      // Add header with gradient-like background
      pdf.setFillColor(59, 130, 246);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Sales Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      
      pdf.setFontSize(8);
      pdf.text('Waziri Mobile Shop', pageWidth / 2, yPosition, { align: 'center' });
      
      yPosition += 25;
      
      // Summary Cards
      pdf.setTextColor(0, 0, 0);
      
      // Revenue Card
      pdf.setFillColor(239, 68, 68);
      pdf.roundedRect(15, yPosition, 80, 35, 3, 3, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Total Revenue', 55, yPosition + 12, { align: 'center' });
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`RS:${actualRevenue.toLocaleString()}`, 55, yPosition + 25, { align: 'center' });
      
      // Sales Card
      pdf.setFillColor(34, 197, 94);
      pdf.roundedRect(105, yPosition, 80, 35, 3, 3, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Total Sales', 145, yPosition + 12, { align: 'center' });
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(actualSales.toString(), 145, yPosition + 25, { align: 'center' });
      
      yPosition += 50;
      
      // Monthly Sales Chart (Simple Bar Chart)
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Monthly Sales Overview', 20, yPosition);
      yPosition += 10;
      
      // Draw simple bar chart
      const chartHeight = 50;
      const chartWidth = pageWidth - 40;
      const barWidth = (chartWidth / Object.keys(monthlySales).length) - 5;
      const maxSale = Math.max(...Object.values(monthlySales), 1);
      
      pdf.setFillColor(240, 240, 240);
      pdf.roundedRect(20, yPosition, chartWidth, chartHeight, 2, 2, 'F');
      
      let barX = 25;
      Object.entries(monthlySales).forEach(([month, amount]) => {
        const barHeight = (amount / maxSale) * (chartHeight - 15);
        pdf.setFillColor(59, 130, 246);
        pdf.rect(barX, yPosition + chartHeight - barHeight - 5, barWidth, barHeight, 'F');
        
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(5);
        pdf.setFont('helvetica', 'normal');
        pdf.text(month, barX + barWidth / 2, yPosition + chartHeight - 2, { align: 'center' });
        pdf.text(`RS:${(amount / 1000).toFixed(1)}k`, barX + barWidth / 2, yPosition + chartHeight - barHeight - 8, { align: 'center' });
        
        barX += barWidth + 5;
      });
      
      yPosition += 60;
      
      // Sales Table
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Sales Details', 20, yPosition);
      yPosition += 10;
      
      // Table header with background
      pdf.setFillColor(59, 130, 246);
      pdf.rect(20, yPosition - 5, pageWidth - 40, 10, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('#', 22, yPosition);
      pdf.text('Date', 35, yPosition);
      pdf.text('Customer', 70, yPosition);
      pdf.text('Amount', 140, yPosition);
      pdf.text('Payment', 175, yPosition);
      
      yPosition += 2;
      
      // Table rows
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      
      salesData?.forEach((sale, index) => {
        if (yPosition > pageHeight - 30) {
          pdf.addPage();
          
          // Add header to new page
          pdf.setFillColor(59, 130, 246);
          pdf.rect(0, 0, pageWidth, 40, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(24);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Sales Report', pageWidth / 2, 20, { align: 'center' });
          
          yPosition = 50;
          
          // Table header
          pdf.setFillColor(59, 130, 246);
          pdf.rect(20, yPosition - 5, pageWidth - 40, 10, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text('#', 22, yPosition);
          pdf.text('Date', 35, yPosition);
          pdf.text('Customer', 70, yPosition);
          pdf.text('Amount', 140, yPosition);
          pdf.text('Payment', 175, yPosition);
          yPosition += 2;
          pdf.setTextColor(0, 0, 0);
        }
        
        yPosition += 8;
        
        // Alternate row colors
        if (index % 2 === 0) {
          pdf.setFillColor(245, 245, 245);
          pdf.rect(20, yPosition - 4, pageWidth - 40, 8, 'F');
        }
        
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text((index + 1).toString(), 22, yPosition);
        pdf.text(new Date(sale.createdAt).toLocaleDateString(), 35, yPosition);
        pdf.text(sale.customer?.name || 'N/A', 70, yPosition);
        pdf.text(`RS:${(sale.total || 0).toLocaleString()}`, 140, yPosition);
        pdf.text(sale.paymentMethod || 'N/A', 175, yPosition);
      });
      
      // Footer
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        pdf.text('© 2024 Waziri Mobile Shop', pageWidth - 20, pageHeight - 10, { align: 'right' });
      }
      
      pdf.save('sales-report.pdf');
      toast.success('Sales report exported successfully');
    } catch (error) {
      toast.error('Failed to export sales report');
      console.error('Export error:', error);
    } finally {
      setExporting(null);
    }
  };

  const exportInventoryToPDF = async () => {
    setExporting('inventory-report.pdf');
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate actual inventory stats
      const totalProducts = inventoryData?.length || 0;
      const totalStock = inventoryData?.reduce((sum, p) => sum + (p.inventory?.stock || 0), 0) || 0;
      const totalSold = inventoryData?.reduce((sum, p) => sum + (p.inventory?.sold || 0), 0) || 0;
      const totalValue = inventoryData?.reduce((sum, p) => sum + ((p.inventory?.stock || 0) * (p.pricing?.salePrice || 0)), 0) || 0;
      
      // Calculate category distribution
      const categoryCount = {};
      inventoryData?.forEach(product => {
        const categoryName = typeof product.category === 'object' 
          ? product.category?.name || 'Unknown'
          : product.category || 'Unknown';
        if (categoryName) {
          categoryCount[categoryName] = (categoryCount[categoryName] || 0) + 1;
        }
      });
      
      let yPosition = 20;
      
      // Add header with gradient-like background
      pdf.setFillColor(168, 85, 247);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Inventory Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      
      pdf.setFontSize(8);
      pdf.text('Waziri Mobile Shop', pageWidth / 2, yPosition, { align: 'center' });
      
      yPosition += 25;
      
      // Summary Cards - Row 1
      pdf.setTextColor(0, 0, 0);
      
      // Products Card
      pdf.setFillColor(59, 130, 246);
      pdf.roundedRect(15, yPosition, 55, 35, 3, 3, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Products', 42.5, yPosition + 12, { align: 'center' });
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(totalProducts.toString(), 42.5, yPosition + 25, { align: 'center' });
      
      // Stock Card
      pdf.setFillColor(34, 197, 94);
      pdf.roundedRect(75, yPosition, 55, 35, 3, 3, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('In Stock', 102.5, yPosition + 12, { align: 'center' });
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(totalStock.toString(), 102.5, yPosition + 25, { align: 'center' });
      
      // Sold Card
      pdf.setFillColor(239, 68, 68);
      pdf.roundedRect(135, yPosition, 55, 35, 3, 3, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Sold', 162.5, yPosition + 12, { align: 'center' });
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(totalSold.toString(), 162.5, yPosition + 25, { align: 'center' });
      
      yPosition += 45;
      
      // Value Card
      pdf.setFillColor(249, 115, 22);
      pdf.roundedRect(15, yPosition, 175, 35, 3, 3, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Total Inventory Value', 102.5, yPosition + 12, { align: 'center' });
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`RS:${totalValue.toLocaleString()}`, 102.5, yPosition + 25, { align: 'center' });
      
      yPosition += 50;
      
      // Category Distribution Chart
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Category Distribution', 20, yPosition);
      yPosition += 10;
      
      // Draw simple category chart
      const chartHeight = 50;
      const chartWidth = pageWidth - 40;
      const barWidth = (chartWidth / Object.keys(categoryCount).length) - 8;
      const maxCount = Math.max(...Object.values(categoryCount), 1);
      
      pdf.setFillColor(240, 240, 240);
      pdf.roundedRect(20, yPosition, chartWidth, chartHeight, 2, 2, 'F');
      
      let barX = 25;
      const colors = [59, 130, 246, 34, 197, 94, 239, 68, 68, 249, 115, 22, 168, 85, 247];
      let colorIndex = 0;
      
      Object.entries(categoryCount).forEach(([category, count]) => {
        const barHeight = (count / maxCount) * (chartHeight - 15);
        pdf.setFillColor(colors[colorIndex % colors.length], colors[(colorIndex + 1) % colors.length], colors[(colorIndex + 2) % colors.length]);
        pdf.roundedRect(barX, yPosition + chartHeight - barHeight - 5, barWidth, barHeight, 2, 2, 'F');
        
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(6);
        pdf.setFont('helvetica', 'normal');
        const shortCategory = category.length > 8 ? category.substring(0, 6) + '..': category;
        pdf.text(shortCategory, barX + barWidth / 2, yPosition + chartHeight - 2, { align: 'center' });
        pdf.text(count.toString(), barX + barWidth / 2, yPosition + chartHeight - barHeight - 8, { align: 'center' });
        
        barX += barWidth + 8;
        colorIndex++;
      });
      
      yPosition += 60;
      
      // Product Details Table
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Product Details', 20, yPosition);
      yPosition += 10;
      
      // Table header with background
      pdf.setFillColor(168, 85, 247);
      pdf.rect(20, yPosition - 5, pageWidth - 40, 10, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('#', 22, yPosition);
      pdf.text('Product', 30, yPosition);
      pdf.text('Category', 90, yPosition);
      pdf.text('Stock', 130, yPosition);
      pdf.text('Sold', 150, yPosition);
      pdf.text('Price', 175, yPosition);
      
      yPosition += 2;
      
      // Table rows
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      
      inventoryData?.forEach((product, index) => {
        if (yPosition > pageHeight - 30) {
          pdf.addPage();
          
          // Add header to new page
          pdf.setFillColor(168, 85, 247);
          pdf.rect(0, 0, pageWidth, 40, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(24);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Inventory Report', pageWidth / 2, 20, { align: 'center' });
          
          yPosition = 50;
          
          // Table header
          pdf.setFillColor(168, 85, 247);
          pdf.rect(20, yPosition - 5, pageWidth - 40, 10, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text('#', 22, yPosition);
          pdf.text('Product', 30, yPosition);
          pdf.text('Category', 90, yPosition);
          pdf.text('Stock', 130, yPosition);
          pdf.text('Sold', 150, yPosition);
          pdf.text('Price', 175, yPosition);
          yPosition += 2;
          pdf.setTextColor(0, 0, 0);
        }
        
        yPosition += 8;
        
        // Alternate row colors
        if (index % 2 === 0) {
          pdf.setFillColor(245, 245, 245);
          pdf.rect(20, yPosition - 4, pageWidth - 40, 8, 'F');
        }
        
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        const categoryName = typeof product.category === 'object' 
          ? product.category?.name || 'N/A' 
          : product.category || 'N/A';
        
        pdf.text((index + 1).toString(), 22, yPosition);
        pdf.text(product.name.substring(0, 20), 30, yPosition);
        pdf.text(categoryName.substring(0, 12), 90, yPosition);
        pdf.text(String(product.inventory?.stock || 0), 130, yPosition);
        pdf.text(String(product.inventory?.sold || 0), 150, yPosition);
        pdf.text(`RS:${(product.pricing?.salePrice || 0).toLocaleString()}`, 175, yPosition);
      });
      
      // Footer
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        pdf.text('© 2024 Waziri Mobile Shop', pageWidth - 20, pageHeight - 10, { align: 'right' });
      }
      
      pdf.save('inventory-report.pdf');
      toast.success('Inventory report exported successfully');
    } catch (error) {
      toast.error('Failed to export inventory report');
      console.error('Export error:', error);
    } finally {
      setExporting(null);
    }
  };

  const getSalesChartData = () => {
    if (!salesData || salesData.length === 0) return null;
    
    const monthlySales = {};
    salesData.forEach(sale => {
      const month = new Date(sale.createdAt).toLocaleString('default', { month: 'short', year: 'numeric' });
      monthlySales[month] = (monthlySales[month] || 0) + sale.total;
    });

    return {
      labels: Object.keys(monthlySales),
      datasets: [
        {
          label: 'Sales Revenue',
          data: Object.values(monthlySales),
          backgroundColor: 'rgba(59, 130, 246, 0.6)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 1,
        },
      ],
    };
  };

  const getInventoryChartData = () => {
    if (!inventoryData || inventoryData.length === 0) return null;
    
    const stockLevels = inventoryData.map(product => ({
      name: product.name.substring(0, 20),
      stock: product.inventory?.stock || 0,
      sold: product.inventory?.sold || 0,
    }));

    return {
      labels: stockLevels.map(item => item.name),
      datasets: [
        {
          label: 'In Stock',
          data: stockLevels.map(item => item.stock),
          backgroundColor: 'rgba(34, 197, 94, 0.6)',
          borderColor: 'rgba(34, 197, 94, 1)',
          borderWidth: 1,
        },
        {
          label: 'Sold',
          data: stockLevels.map(item => item.sold),
          backgroundColor: 'rgba(239, 68, 68, 0.6)',
          borderColor: 'rgba(239, 68, 68, 1)',
          borderWidth: 1,
        },
      ],
    };
  };

  const getCategoryData = () => {
    if (!inventoryData || inventoryData.length === 0) return null;
    
    const categoryCount = {};
    inventoryData.forEach(product => {
      const categoryName = typeof product.category === 'object' 
        ? product.category?.name || 'Unknown' 
        : product.category || 'Unknown';
      if (categoryName) {
        categoryCount[categoryName] = (categoryCount[categoryName] || 0) + 1;
      }
    });

    return {
      labels: Object.keys(categoryCount),
      datasets: [
        {
          data: Object.values(categoryCount),
          backgroundColor: [
            'rgba(59, 130, 246, 0.6)',
            'rgba(34, 197, 94, 0.6)',
            'rgba(239, 68, 68, 0.6)',
            'rgba(249, 115, 22, 0.6)',
            'rgba(168, 85, 247, 0.6)',
            'rgba(236, 72, 153, 0.6)',
          ],
          borderColor: [
            'rgba(59, 130, 246, 1)',
            'rgba(34, 197, 94, 1)',
            'rgba(239, 68, 68, 1)',
            'rgba(249, 115, 22, 1)',
            'rgba(168, 85, 247, 1)',
            'rgba(236, 72, 153, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  if (loading) {
    return <div className="text-center py-20">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Reports</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <FaChartBar className="text-blue-600" />
            </div>
            <div>
              <div className="text-lg font-bold">RS:{salesData?.reduce((sum, sale) => sum + (sale.total || 0), 0)?.toLocaleString() || 0}</div>
              <div className="text-sm text-gray-500">Total Revenue</div>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <FaChartBar className="text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats?.totalSales || 0}</div>
              <div className="text-sm text-gray-500">Total Sales</div>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
              <FaChartBar className="text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats?.totalProducts || 0}</div>
              <div className="text-sm text-gray-500">Products</div>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
              <FaChartBar className="text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats?.totalCustomers || 0}</div>
              <div className="text-sm text-gray-500">Customers</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Sales Report</h2>
            <button
              onClick={exportSalesToPDF}
              disabled={exporting === 'sales-report.pdf'}
              className="btn-primary flex items-center gap-2"
            >
              {exporting === 'sales-report.pdf' ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaDownload />
              )}
              {exporting === 'sales-report.pdf' ? 'Exporting...' : 'Export Sales Report'}
            </button>
          </div>
          
          {/* Preview */}
          <div className="space-y-4">
            <div className="h-64">
              {getSalesChartData() ? (
                <Line
                  data={getSalesChartData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                      },
                    },
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No sales data available
                </div>
              )}
            </div>
            <div className="text-sm text-gray-500">
              Total Sales: {salesData?.length || 0} | Revenue: RS:{salesData?.reduce((sum, sale) => sum + (sale.total || 0), 0)?.toLocaleString() || 0}
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Inventory Report</h2>
            <button
              onClick={exportInventoryToPDF}
              disabled={exporting === 'inventory-report.pdf'}
              className="btn-primary flex items-center gap-2"
            >
              {exporting === 'inventory-report.pdf' ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaDownload />
              )}
              {exporting === 'inventory-report.pdf' ? 'Exporting...' : 'Export Inventory Report'}
            </button>
          </div>

          {/* Preview */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="h-48">
                {getInventoryChartData() ? (
                  <Bar
                    data={getInventoryChartData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: true,
                        },
                      },
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    No inventory data available
                  </div>
                )}
              </div>
              <div className="h-48">
                {getCategoryData() ? (
                  <Pie
                    data={getCategoryData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: true,
                        },
                      },
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    No category data available
                  </div>
                )}
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Total Products: {inventoryData?.length || 0} | In Stock: {inventoryData?.reduce((sum, p) => sum + (p.inventory?.stock || 0), 0) || 0}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
